// lib/notification/toast-manager.ts
"use client";

import { toast, type ExternalToast, type ToasterProps } from 'sonner';

/* ============================================================
   | TYPES & INTERFACES
   ============================================================ */

export type ToastIntent = 'success' | 'error' | 'info' | 'warning' | 'loading';

// Updated interface to extend ExternalToast properly
interface CustomToastOptions extends ExternalToast {
  description?: string;
  duration?: number;
  dismissible?: boolean;
}

interface ToastWithMessage {
  message: string;
  options?: CustomToastOptions;
}

interface AsyncToastOptions<T> {
  loading: string | ToastWithMessage;
  success: string | ((data: T) => string | ToastWithMessage);
  error: string | ((err: unknown) => string | ToastWithMessage);
}

/* ============================================================
   | TOASTER CONFIGURATION
   ============================================================ */

/**
 * Toaster configuration for consistent Apple-inspired styling
 */
export const getToasterConfig = (): Partial<ToasterProps> => ({
  className: `
    !backdrop-blur-md
    !bg-white/95
    !border !border-gray-200/80
    !shadow-2xl
    !rounded-xl
    !px-4 !py-3
    !text-sm !font-medium
    !max-w-[92vw] sm:!max-w-md
    !transition-all !duration-200
  `,
  position: 'top-right',
  duration: 4000,
  expand: false,
  gap: 8,
  visibleToasts: 3,
});

/* ============================================================
   | USER-FRIENDLY MESSAGE FILTER
   ============================================================ */

const USER_FRIENDLY_MESSAGES = {
  // Network & Connectivity
  network: {
    patterns: [/network.*error/i, /connection.*failed/i, /offline/i, /timeout/i],
    message: "Network connection issue. Please check your internet and try again.",
  },
  server: {
    patterns: [/server.*error/i, /internal.*error/i, /500/i, /502/i, /503/i, /504/i],
    message: "Service temporarily unavailable. Please try again in a moment.",
  },
  auth: {
    patterns: [/unauthorized/i, /forbidden/i, /session.*expired/i],
    message: "Session expired. Please sign in again.",
  },
  validation: {
    patterns: [/validation.*failed/i, /invalid.*input/i],
    message: "Please check your information and try again.",
  },
  rateLimit: {
    patterns: [/rate.*limit/i, /too.*many.*requests/i],
    message: "Too many attempts. Please wait a moment before trying again.",
  },
  duplicate: {
    patterns: [/already.*exists/i, /duplicate.*entry/i],
    message: "This information is already in use. Please use different details.",
  },
  notFound: {
    patterns: [/not.*found/i, /404/i, /does.*not.*exist/i],
    message: "The requested resource was not found.",
  },
} as const;

function getUserFriendlyMessage(error: string | Error): string {
  const errorMessage = error instanceof Error ? error.message : error;
  const lowerError = errorMessage.toLowerCase();
  
  for (const config of Object.values(USER_FRIENDLY_MESSAGES)) {
    if (config.patterns.some(pattern => pattern.test(lowerError))) {
      return config.message;
    }
  }
  
  // Default user-friendly message
  return "Something went wrong. Please try again.";
}

/* ============================================================
   | TOAST MANAGER (Production Ready)
   ============================================================ */

class ToastManager {
  private defaultDuration = 4000;

  /**
   * Core toast method with proper error handling
   */
  private createToast(
    intent: ToastIntent,
    message: string,
    options?: CustomToastOptions
  ): string | number {
    try {
      // Configure toast options
      const toastOptions: ExternalToast = {
        duration: options?.duration || this.defaultDuration,
        description: options?.description,
        dismissible: options?.dismissible ?? true,
        closeButton: true,
        ...options,
      };

      // Dispatch based on intent
      switch (intent) {
        case 'success':
          return toast.success(message, toastOptions);
        case 'error':
          return toast.error(message, toastOptions);
        case 'warning':
          return toast.warning(message, toastOptions);
        case 'info':
          return toast.info(message, toastOptions);
        case 'loading':
          return toast.loading(message, { ...toastOptions, duration: Infinity });
        default:
          return toast(message, toastOptions);
      }
    } catch (error) {
      console.error('Toast creation error:', error);
      // Fallback to basic toast
      return toast(message);
    }
  }

  /**
   * Success toast
   */
  success(message: string, options?: CustomToastOptions): string | number {
    return this.createToast('success', message, {
      ...options,
      duration: 3000,
    });
  }

  /**
   * Error toast with smart message handling
   */
  error(error: string | Error, options?: CustomToastOptions): string | number {
    const friendlyMessage = getUserFriendlyMessage(error);
    const errorMessage = error instanceof Error ? error.message : error;
    
    const toastOptions: CustomToastOptions = {
      ...options,
      duration: 5000,
      description: errorMessage !== friendlyMessage ? errorMessage.substring(0, 120) : undefined,
    };

    return this.createToast('error', friendlyMessage, toastOptions);
  }

  /**
   * Info toast
   */
  info(message: string, options?: CustomToastOptions): string | number {
    return this.createToast('info', message, {
      ...options,
      duration: 4000,
    });
  }

  /**
   * Warning toast
   */
  warning(message: string, options?: CustomToastOptions): string | number {
    return this.createToast('warning', message, {
      ...options,
      duration: 4500,
    });
  }

  /**
   * Loading toast
   */
  loading(message: string, options?: CustomToastOptions): string | number {
    return this.createToast('loading', message, {
      ...options,
      duration: Infinity,
    });
  }

  /**
   * Promise-based toast with proper typing
   */
  async async<T>(
    promise: Promise<T>,
    options: AsyncToastOptions<T>
  ): Promise<T> {
    const loadingConfig: ToastWithMessage =
      typeof options.loading === 'string'
        ? { message: options.loading }
        : options.loading;

    const toastId = this.loading(
      loadingConfig.message,
      loadingConfig.options
    );

    try {
      const data = await promise;

      // Get success configuration
      const successResult = typeof options.success === 'function'
        ? options.success(data)
        : options.success;

      const successConfig: ToastWithMessage =
        typeof successResult === 'string'
          ? { message: successResult }
          : successResult;

      // Dismiss loading and show success
      this.dismiss(toastId);
      this.success(successConfig.message, successConfig.options);

      return data;
    } catch (error) {
      // Get error configuration
      const errorResult = typeof options.error === 'function'
        ? options.error(error)
        : options.error;

      const errorConfig: ToastWithMessage =
        typeof errorResult === 'string'
          ? { message: errorResult }
          : errorResult;

      // Dismiss loading and show error
      this.dismiss(toastId);
      this.error(errorConfig.message, errorConfig.options);

      throw error;
    }
  }

  /**
   * Dismiss toast
   */
  dismiss(id?: string | number): void {
    toast.dismiss(id);
  }

  /**
   * Dismiss all toasts
   */
  dismissAll(): void {
    toast.dismiss();
  }
}

/* ============================================================
   | EXPORT SINGLETON INSTANCE & UTILITIES
   ============================================================ */

export const toastManager = new ToastManager();

// Convenience exports with proper typing
export const notify = {
  // Basic toasts
  success: (message: string, options?: CustomToastOptions) => 
    toastManager.success(message, options),
  error: (error: string | Error, options?: CustomToastOptions) => 
    toastManager.error(error, options),
  info: (message: string, options?: CustomToastOptions) => 
    toastManager.info(message, options),
  warning: (message: string, options?: CustomToastOptions) => 
    toastManager.warning(message, options),
  loading: (message: string, options?: CustomToastOptions) => 
    toastManager.loading(message, options),
  
  // Async operations
  async: <T>(promise: Promise<T>, options: AsyncToastOptions<T>) => 
    toastManager.async(promise, options),
  
  // Management
  dismiss: (id?: string | number) => toastManager.dismiss(id),
  dismissAll: () => toastManager.dismissAll(),
};

// Type exports for external use
export type { CustomToastOptions as ToastOptions };